import {useSelector} from 'react-redux'
import {maxCallQueueSelector, selectedSiteSelector} from '../authentication/redux'
import React, {ReactElement, useMemo} from 'react'
import {AnswerGroup} from '../app/types'
import {ColumnDefinition} from '../common/components/table/Table'
import {useTranslation} from 'react-i18next'
import {useFlowTable} from '../flows/useFlowTable'
import {formatPhoneNumber, formatTotalStat} from '../common/util/formatters'
import {Avatar, Card, CardContent, CardHeader, CardMedia, Link, Typography} from '@mui/material'
import {detailUrl} from '../flows'
import {AddButton} from '../common/components/AddButton'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import {usePrivilege} from '../common/util/hooks'
import {FeaturePrivileges} from '../common/util/featurePrivileges'

export const useFlowStats = ({ onClick, type, onlyPremium }: { onClick: () => void, type: string, onlyPremium?: boolean}): { stats: ReactElement; totalFlows: number } => {
    const site = useSelector(selectedSiteSelector)
    const { t } = useTranslation()
    const maxCallQueues = useSelector(maxCallQueueSelector)
    const { onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.callFlows.write)
    const { hasAccess: canLinkToFlows } = usePrivilege(FeaturePrivileges.callFlows.read)

    const columns: ColumnDefinition<AnswerGroup>[] = useMemo(
        () => [
            {
                title: t('site.cards.flows.table.name'),
                key: 'name',
                sorter: true,
                render: (text: string, record: AnswerGroup) => canLinkToFlows
                    ? <Link href={detailUrl.replace(':id', record.uuid)}>{record.name}</Link>
                    : <>{record.name}</>,
            },
            {
                title: t('site.cards.flows.table.number'),
                key: 'number',
                sorter: true,
                render: (text: string) => <>{formatPhoneNumber(text)}</>,
            },
        ],
        [t, canLinkToFlows],
    )

    const { table, totalElements } = useFlowTable({
        columns: columns,
        queryKey: `answerGroups-${type}-${site?.productId}-${onlyPremium || ''}-stats`,
        initialSortField: 'name',
        maxHeight: '18vh',
        type: type,
        onlyPremium: onlyPremium
    })

    return {
        stats: (
            <Card sx={{ height: '100%' }}>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: theme => theme.palette.primary.main, color: theme => theme.palette.secondary.main }} variant="rounded">
                            <CallSplitIcon />
                        </Avatar>
                    }
                    title={canLinkToFlows
                        ? <Link href={'/flows'}>{t(type === 'QUEUE' ? (onlyPremium? 'general.warnings.extension-update.premium-call-centers' : 'enterprise-sites.table.queues') : 'enterprise-sites.table.hunt-groups')} </Link>
                        : <>{t(type === 'QUEUE' ? (onlyPremium? 'general.warnings.extension-update.premium-call-centers' : 'enterprise-sites.table.queues') : 'enterprise-sites.table.hunt-groups')}</>}
                    subheader={
                        <Typography color={type === 'QUEUE' && maxCallQueues !== -1 &&  totalElements > maxCallQueues ? 'error.main' : 'text.secondary'} sx={{ fontSize: 14 }}>
                            {`# ${type === 'QUEUE' && !onlyPremium ? formatTotalStat(totalElements, maxCallQueues) : totalElements}`}
                        </Typography>
                    }
                    action={onlyWithWriteAccess(<AddButton small={true} onClick={onClick} label={t('flows.create-new')} />)}
                />
                <CardMedia></CardMedia>
                <CardContent>{table}</CardContent>
            </Card>
        ),
        totalFlows: totalElements,
    }
}
